import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";

import {
    fetchRoles,
    fetchRoleColumns,
    setSelectedIds as setSelectedIdsAction,
    setSearchString as setSearchStringAction,
    setPageNumber as setPageNumberAction,
    setLimitNumber as setLimitNumberAction,
    setSortData as setSortDataAction,
    setFilterData as setFilterDataAction,
    setError as setErrorAction,
} from "../../../../reducers/roleSlice";

import useFilters from "../../../common/hooks/useFilters";
import useReduxTable from "../../../common/hooks/useReduxTable";
import useRiskLevels from "../../../common/hooks/useRiskLevels";
import CommonTable from "../../../common/table";
import { ProgressDialog, ErrorsDialog } from "../../../common/dialogs";

import "./styles.less";


const getCellValue = (row, column, intl, riskLevels) => {
    switch (column.id) {
        case "systemId":
            return row.system;

        case "critRoleName":
            return row.name;

        case "riskLevelId": {
            const riskLevel = riskLevels.find(riskLevel => riskLevel.id === row.riskLevel);
            return riskLevel?.description || row.riskLevel;
        }
        case "updUserName":
            return row.updUser;

        case "matrices":
            return row.matrices.map(matrix => matrix.key).join(", ");


        case "active":
            return row.active ?
                intl.formatMessage({ id: "common.table.active.true" }) :
                intl.formatMessage({ id: "common.table.active.false" });

        default:
            return row[column.id];
    }
};

const RoleListPage = () => {
    const intl = useIntl();
    const navigate = useNavigate();

    const riskLevels = useRiskLevels();

    const {
        rows, columns,
        total, busy,
        selectedIds,
        searchString, setSearchString,
        pageNumber, setPageNumber,
        limitNumber, setLimitNumber,
        filterData, setFilterData,
        sortData, setSortData,
        error, setError
    } = useReduxTable(
        (state) => state.roles.roles, fetchRoles,
        (state) => state.roles.columns, fetchRoleColumns,
        (state) => state.roles.selectedIds, setSelectedIdsAction,
        (state) => state.roles.searchString, setSearchStringAction,
        (state) => state.roles.pageNumber, setPageNumberAction,
        (state) => state.roles.limitNumber, setLimitNumberAction,
        (state) => state.roles.sortData, setSortDataAction,
        (state) => state.roles.filterData, setFilterDataAction,
        (state) => state.roles.error, setErrorAction,
        (state) => state.roles.rolesTotal,
        (state) => state.roles.busy,
    );

    const busyType = useSelector((state) => state.roles.busyType);

    const getFilters = useFilters("critical-role");

    const handleAddRoleClick = () => {
        navigate("form")
    };

    const handleRowClick = (role) => {
        const encodedName = encodeURIComponent(role.name);
        navigate(`form/${encodedName}/${role.system}`);
    };

    const getTableRowId = (row) => `${row.system}${row.name}`;

    const getCellValueInner = (row, column) => getCellValue(row, column, intl, riskLevels);

    const toolbarActions = [
        {
            id: "title",
            title: intl.formatMessage({ id: "role-list-page.toolbar.text.title" }),
            type: "text",
            position: "begin"
        },
        {
            id: "add",
            title: intl.formatMessage({ id: "role-list-page.toolbar.btn-add-role" }),
            callback: handleAddRoleClick,
            position: "end"
        }
    ];

    return (
        <>
            <CommonTable
                rows={rows}
                columns={columns}
                toolbarActions={toolbarActions}
                selectedIds={selectedIds}
                filterData={filterData}
                setFilterData={setFilterData}
                sortData={sortData}
                setSortData={setSortData}
                getRowId={getTableRowId}
                pageNumber={pageNumber}
                setPageNumber={setPageNumber}
                limitNumber={limitNumber}
                setLimitNumber={setLimitNumber}
                total={total}
                onRowClick={handleRowClick}
                searchString={searchString}
                setSearchString={setSearchString}
                getCellValue={getCellValueInner}
                getFilters={getFilters}
            />

            <ProgressDialog
                open={busy}
                busyType={busyType}
            />

            <ErrorsDialog
                error={error}
                open={error?.type === "error"}
                onClose={() => setError(null)}
            />
        </>
    )
};

export default RoleListPage;